import moment from 'moment-timezone';
import { getTimeInHrMinAndSec } from '../Utils/DateFormatUtils';
import i18n from '../Utils/i18n';
import { getPercentageSafe } from '../Utils/MathUtils';
import { RecordingSource, SortOrder, ReportType } from './Constants';
import { getSafeTimezone } from './MomentUtils';
import { getRecordingSource } from '../GoToWebinar/Recordings/Utils/RecordingsContainerUtils';
import { AnswererRoles } from './Constants';

export function getSessionsData(sessions) {
  if (!sessions || sessions.length === 0) {
    return [];
  } else {
    return sessions.map(
      ({
        sessionKey,
        webinarName,
        creatingOrganizerName,
        creator,
        startTime,
        registrantCount,
        registrantsAttended,
        timeZone,
        sessionStatsInfo
      }) => {
        let tz = getSafeTimezone(timeZone);
        let startTimeAndZoneObject = { dateTime: startTime, timeZone: tz };
        const { sessionQAndACount = 0, attendeePollResponseCount = 0, surveyQuestionResponseCount = 0 } =
          sessionStatsInfo || {};
        let engagementCount = sessionQAndACount + attendeePollResponseCount + surveyQuestionResponseCount;
        return {
          sessionKey,
          organizerName: creatingOrganizerName,
          organizerEmail: creator && creator.email,
          name: webinarName,
          day: startTimeAndZoneObject,
          date: startTimeAndZoneObject,
          startTime: startTimeAndZoneObject,
          registrants: registrantCount,
          attendees: registrantsAttended,
          attendanceRate: getPercentageSafe(registrantsAttended, registrantCount),
          engagementCount: engagementCount,
          qAndAResponseCount: sessionQAndACount,
          pollResponses: attendeePollResponseCount,
          surveyResponses: surveyQuestionResponseCount
        };
      }
    );
  }
}

export function getVirtualEventsData(virtualEvents, reportType) {
  if (!virtualEvents || virtualEvents.length === 0) {
    return [];
  } else {
    if (reportType === ReportType.VIRTUAL_EVENT_EXECUTIVE_SUMMARY_REPORT) {
      // filter event which endTime is already past by 1 hour
      virtualEvents = virtualEvents.filter(ve => new Date() - new Date(ve.end) > 3600000);
    }
    return virtualEvents.map(({ eventKey, title, start, registrationCount, end, timezone }) => {
      let tz = getSafeTimezone(timezone);
      let startTimeAndZoneObject = { dateTime: start, timeZone: tz };
      let endTimeAndZoneObject = { dateTime: end, timeZone: tz };

      return {
        virtualEventKey: eventKey,
        name: title,
        day: startTimeAndZoneObject,
        date: startTimeAndZoneObject,
        startTime: startTimeAndZoneObject,
        registrants: registrationCount && registrationCount.confirmed ? registrationCount.confirmed : 0,
        endTime: endTimeAndZoneObject
      };
    });
  }
}

export function getWebinarsData(webinars) {
  if (!webinars || webinars.length === 0) {
    return [];
  } else {
    return webinars.map(
      ({ webinarKey, webinarId, subject, startTime, registrationStatisticsInfo, timeZone, creator, staff }) => {
        let tz = getSafeTimezone(timeZone);
        let startTimeAndZoneObject = { dateTime: startTime, timeZone: tz };
        return {
          webinarKey,
          webinarId,
          organizerName: creator && `${creator.givenName} ${creator.surName}`,
          organizerEmail: creator && creator.email,
          name: subject,
          date: startTimeAndZoneObject,
          startTime: startTimeAndZoneObject,
          staff,
          registrants: registrationStatisticsInfo.approvedCount ? registrationStatisticsInfo.approvedCount : 0
        };
      }
    );
  }
}

export function getRecordingsData(recordings, reportSource) {
  if (!recordings || recordings.length === 0) {
    return [];
  } else {
    return recordings
      .filter(function(recording) {
        return reportSource.includes(recording.source);
      })
      .map(
        ({
          recordingKey,
          title,
          dateUploaded,
          viewCount,
          shareCount,
          length,
          avgWatchTime,
          source,
          givenName,
          surName,
          email
        }) => {
          let timeZone = 'UTC';
          let timeObject = { dateTime: dateUploaded, timeZone };
          return {
            recordingKey,
            organizerName: `${givenName} ${surName}`,
            organizerEmail: email,
            viewCount,
            name: title,
            dateUploaded: timeObject,
            shareCount: source === RecordingSource.G2S ? shareCount : 'NA',
            length: source === RecordingSource.G2S ? length : 'NA',
            avgWatchTime: source === RecordingSource.G2S ? avgWatchTime : 'NA',
            source: getRecordingSource(source)
          };
        }
      );
  }
}

export function getAttendeesData(attendees) {
  if (!attendees || attendees.length === 0) {
    return [];
  } else {
    return attendees.map(({ registrantKey, stats, givenName, surName, email, certificateSent }) => {
      const {
        interestRating = 0,
        pollResponseCount = 0,
        surveyQuestionAnsweredCount = 0,
        sessionQuestionCount = 0,
        attendanceLength = 0
      } = stats || {};
      return {
        registrantKey,
        interestRating,
        email,
        certificateSent,
        pollResponseCount,
        surveyQuestionAnsweredCount,
        sessionQuestionCount,
        firstName: givenName,
        lastName: surName,
        timeInSession: attendanceLength
      };
    });
  }
}

export function getRegistrantsData(registrants) {
  if (!registrants || registrants.length === 0) {
    return [];
  } else {
    return registrants.map(({ givenName, surName, email, createTime, subscribed, status, source, timeZone }) => {
      return {
        firstName: givenName,
        lastName: surName,
        registrationDateAndTime: getFormattedTime(createTime, timeZone),
        unsubscribed: subscribed ? i18n.t('sessionDetails.no') : i18n.t('sessionDetails.yes'),
        source: source ? source : 'n/a',
        email,
        status
      };
    });
  }
}

export function getFormattedTime(time, timeZone) {
  let tz = getSafeTimezone(timeZone);
  let fullDateTimeFormat = i18n.t('formatting.dayDateTimeWithMeridianCapital');
  let timeString = moment(time)
    .tz(tz)
    .format(fullDateTimeFormat);

  return `${timeString}`;
}

export function timeFormatterInHrMinSec(cell) {
  return isNaN(cell.value) ? cell.value : cell.value ? getTimeInHrMinAndSec(cell.value) : 'NA';
}

export function percentageFormatter(cell) {
  return isFinite(cell.value) ? `${Math.round(cell.value)} %` : '-';
}

export function timeFormatterFromMilliSecToHrMinSec(cell) {
  return isNaN(cell.value) ? cell.value : cell.value ? getTimeInHrMinAndSec(cell.value / 1000) : 'NA';
}

export function daySorter(first, second, desc) {
  let { dateTime: firstStartTime, timeZone: firstTimeZone } = first;
  let { dateTime: secondStartTime, timeZone: secondTimeZone } = second;

  let firstStartDateTime = moment.tz(firstStartTime, firstTimeZone);
  let secondStartDateTime = moment.tz(secondStartTime, secondTimeZone);

  let firstDay = firstStartDateTime.day();
  let secondDay = secondStartDateTime.day();

  firstDay = firstDay === 0 ? 7 : firstDay;
  secondDay = secondDay === 0 ? 7 : secondDay;

  if (firstDay === secondDay) {
    if (desc === false) {
      return firstStartDateTime.valueOf() > secondStartDateTime.valueOf() ? -1 : 1;
    }
    return firstStartDateTime.valueOf() > secondStartDateTime.valueOf() ? 1 : -1;
  }
  return firstDay > secondDay ? 1 : -1;
}

export function dateSorter(first, second) {
  let { dateTime: firstStartTime, timeZone: firstTimeZone } = first;
  let { dateTime: secondStartTime, timeZone: secondTimeZone } = second;

  let firstStartDateTime = moment.tz(firstStartTime, firstTimeZone);
  let secondStartDateTime = moment.tz(secondStartTime, secondTimeZone);

  return firstStartDateTime.valueOf() > secondStartDateTime.valueOf() ? 1 : -1;
}

export function startTimeSorter(first, second) {
  let { dateTime: firstStartTime, timeZone: firstTimeZone } = first;
  let { dateTime: secondStartTime, timeZone: secondTimeZone } = second;

  let firstStartDateTime = moment.tz(firstStartTime, firstTimeZone);
  let secondStartDateTime = moment.tz(secondStartTime, secondTimeZone);

  let firstMinuteMidnight = firstStartDateTime.clone().startOf('day');
  let secondMinuteMidnight = secondStartDateTime.clone().startOf('day');

  let firstMinutesSinceMidnight = firstStartDateTime.diff(firstMinuteMidnight, 'seconds');
  let secondMinutesSinceMidnight = secondStartDateTime.diff(secondMinuteMidnight, 'seconds');
  return firstMinutesSinceMidnight > secondMinutesSinceMidnight ? 1 : -1;
}

export function endTimeSorter(first, second) {
  let { dateTime: firstEndTime, timeZone: firstTimeZone } = first;
  let { dateTime: secondEndTime, timeZone: secondTimeZone } = second;

  let firstEndDateTime = moment.tz(firstEndTime, firstTimeZone);
  let secondEndDateTime = moment.tz(secondEndTime, secondTimeZone);

  let firstMinuteMidnight = firstEndDateTime.clone().startOf('day');
  let secondMinuteMidnight = secondEndDateTime.clone().startOf('day');

  let firstMinutesSinceMidnight = firstEndDateTime.diff(firstMinuteMidnight, 'seconds');
  let secondMinutesSinceMidnight = secondEndDateTime.diff(secondMinuteMidnight, 'seconds');
  return firstMinutesSinceMidnight > secondMinutesSinceMidnight ? 1 : -1;
}

export function webinarIdFormatter(id, key) {
  const value = id === key ? '' : id.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3');
  return value;
}

export function coorgsFormatter(data) {
  const staffList = data.staff;
  const allowedRoles = [AnswererRoles.INTERNAL_ORGANIZER, AnswererRoles.EXTERNAL_ORGANIZER];

  const coorgs = staffList
    .filter(staff => allowedRoles.includes(staff.role) && staff.email !== data.organizerEmail)
    .map(staff => (staff.surName ? `${staff.givenName} ${staff.surName}` : staff.givenName));

  return coorgs.join(', ');
}

export function dayFormatter(cell) {
  return moment(cell.value.dateTime)
    .tz(cell.value.timeZone)
    .format(i18n.t('formatting.dayLower'));
}

export function dateFormatter(cell) {
  return moment(cell.value.dateTime)
    .tz(cell.value.timeZone)
    .format(i18n.t('formatting.date'));
}

export function dayDateFormatterWithTimezone(isotime, timeZone) {
  if (isotime && timeZone) {
    return moment(isotime)
      .tz(timeZone)
      .format(i18n.t('formatting.dayDate'));
  } else {
    return null;
  }
}

export function dateFormatterWithTimezone(isotime, timeZone) {
  if (isotime && timeZone) {
    return moment(isotime)
      .tz(timeZone)
      .format(i18n.t('formatting.date'));
  } else {
    return null;
  }
}

export function startTimeFormatter(cell) {
  return moment(cell.value.dateTime)
    .tz(cell.value.timeZone)
    .format(i18n.t('formatting.timeWithZone'));
}

export function endTimeFormatter(cell) {
  return moment(cell.value.dateTime)
    .tz(cell.value.timeZone)
    .format(i18n.t('formatting.timeWithZone'));
}

export function attendanceRateFormatter(cell) {
  return cell.value + '%';
}

export function getInverseOrder(sortOrder) {
  switch (sortOrder) {
    case SortOrder.ASC:
      return SortOrder.DESC;
    case SortOrder.DESC:
      return SortOrder.ASC;
    default:
      return null;
  }
}



// WEBPACK FOOTER //
// ./src/Utils/TableUtil.js